import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "@/i18n/translations";

const WD = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const defaultRepeat = (date) => ({
  every: 1, unit: "week", weekdays: [((new Date(date + "T00:00").getDay() + 6) % 7)], end_type: "never", count: 5, until: "",
});

export default function RepeatRuleEditor({ value, onChange }) {
  const { t } = useTranslation();
  const patch = (p) => onChange({ ...value, ...p });
  const toggleDay = (i) => {
    const set = new Set(value.weekdays);
    set.has(i) ? set.delete(i) : set.add(i);
    patch({ weekdays: [...set].sort() });
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3" data-testid="cal-repeat-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="whitespace-nowrap">{t("cal.repeatEvery")}</Label>
        <Input data-testid="cal-repeat-every" type="number" min="1" max="12" value={value.every}
          onChange={(e) => patch({ every: Math.max(1, Math.min(12, parseInt(e.target.value) || 1)) })} className="h-9 w-16" />
        <Select value={value.unit} onValueChange={(v) => patch({ unit: v })}>
          <SelectTrigger data-testid="cal-repeat-unit" className="h-9 w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">{t("cal.unitWeeks")}</SelectItem>
            <SelectItem value="day">{t("cal.unitDays")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {value.unit === "week" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="whitespace-nowrap">{t("cal.onDays")}</Label>
          <div className="flex rounded-md overflow-hidden border border-gray-300" data-testid="cal-repeat-weekdays">
            {WD.map((k, i) => (
              <button key={k} type="button" data-testid={`cal-repeat-wd-${k}`} onClick={() => toggleDay(i)}
                className={`w-9 h-9 text-xs font-semibold transition-colors ${value.weekdays.includes(i) ? "bg-green-600 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}>
                {t(`cal.wd.${k}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-sm">
        <Label className="whitespace-nowrap">{t("cal.repeatEnd")}</Label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name="repeat-end" data-testid="cal-repeat-end-never" checked={value.end_type === "never"} onChange={() => patch({ end_type: "never" })} />
          {t("cal.endNever")}
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name="repeat-end" data-testid="cal-repeat-end-after" checked={value.end_type === "after"} onChange={() => patch({ end_type: "after" })} />
          {t("cal.endAfter")}
          <Input data-testid="cal-repeat-count" type="number" min="1" max="200" value={value.count} disabled={value.end_type !== "after"}
            onChange={(e) => patch({ count: Math.max(1, parseInt(e.target.value) || 1) })} className="h-8 w-16" />
          {t("cal.endTimes")}
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="radio" name="repeat-end" data-testid="cal-repeat-end-on" checked={value.end_type === "on"} onChange={() => patch({ end_type: "on" })} />
          {t("cal.endOn")}
          <Input data-testid="cal-repeat-until" type="date" value={value.until} disabled={value.end_type !== "on"}
            onChange={(e) => patch({ until: e.target.value })} className="h-8 w-40" />
        </label>
      </div>
      {value.end_type === "never" && <p className="text-[11px] text-gray-500">{t("cal.endNeverHint")}</p>}
    </div>
  );
}
